import { OverviewOutputBodyV2, TotalStats } from "../../api/generated";
import { useIsMobile } from "../../hooks";
import {
  ApprovedNotFundedIcon,
  FundedNotTradedIcon,
  TotalActiveClientsIcon,
  TotalClientsIcon,
  TotalEarningsIcon,
} from "../../Icons";
import { OverviewInfoCard } from "./OverviewInfoCard";
import { OverviewInfoDashboardSkeleton } from "./OverviewInfoDashboardSkeleton";

export type OverviewInfoDashboardProps = {
  data: OverviewOutputBodyV2 | undefined;
  isLoading: boolean;
  translatedContent: OverviewInfoDashboardTranslatedContent;
  isBackoffice?: boolean;
};

type OverviewInfoDashboardTranslatedContent = {
  totalClients: string;
  totalEarnings: string;
  totalActiveClients: string;
  fundedNotTraded: string;
  approvedNotFunded: string;
  period: string;
  totalEarningsTooltip: string;
  totalActiveClientsTooltip: string;
  fundedNotTradedTooltip: string;
  approvedNotFundedTooltip: string;
  currencyUSD: string;
};

type StaticHeadlineMetricData = {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  key: keyof TotalStats;
  title: string;
  tooltipText?: string;
  value: number;
  changeInPercent: number;
  unit?: string;
  error?: string;
};

export const OverviewInfoDashboard = ({
  data,
  isLoading,
  translatedContent,
  isBackoffice = false,
}: OverviewInfoDashboardProps) => {
  const staticHeadlineMetricData: StaticHeadlineMetricData[] = [
    {
      icon: TotalClientsIcon,
      key: "clients",
      title: translatedContent.totalClients,
      value: data?.totalStats.clients.value ?? 0,
      changeInPercent: data?.totalStats.clients.changeInPercent ?? 0,
      unit: data?.totalStats.clients.unit,
    },
    {
      icon: TotalEarningsIcon,
      key: "earnings",
      title: translatedContent.totalEarnings,
      value: data?.totalStats.earnings.value ?? 0,
      changeInPercent: data?.totalStats.earnings.changeInPercent ?? 0,
      unit: translatedContent.currencyUSD,
      error: data?.totalStats.earnings.error,
      tooltipText: translatedContent.totalEarningsTooltip,
    },
    {
      icon: TotalActiveClientsIcon,
      key: "activeClients",
      title: translatedContent.totalActiveClients,
      value: data?.totalStats.activeClients.value ?? 0,
      changeInPercent: data?.totalStats.activeClients.changeInPercent ?? 0,
      unit: data?.totalStats.activeClients.unit,
      tooltipText: translatedContent.totalActiveClientsTooltip,
    },
    {
      icon: FundedNotTradedIcon,
      key: "fundedNotTraded",
      title: translatedContent.fundedNotTraded,
      value: data?.totalStats.fundedNotTraded.value ?? 0,
      changeInPercent: data?.totalStats.fundedNotTraded.changeInPercent ?? 0,
      unit: data?.totalStats.fundedNotTraded.unit,
      tooltipText: translatedContent.fundedNotTradedTooltip,
    },
    {
      icon: ApprovedNotFundedIcon,
      key: "approvedNotFunded",
      title: translatedContent.approvedNotFunded,
      value: data?.totalStats.approvedNotFunded.value ?? 0,
      changeInPercent: data?.totalStats.approvedNotFunded.changeInPercent ?? 0,
      unit: data?.totalStats.approvedNotFunded.unit,
      tooltipText: translatedContent.approvedNotFundedTooltip,
    },
  ];

  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <OverviewInfoDashboardSkeleton
        staticHeadlineMetricData={staticHeadlineMetricData}
        isMobile={isMobile}
      />
    );
  }

  if (isMobile) {
    return (
      <div className="grid grid-cols-2 items-stretch justify-start gap-x-6 gap-y-8 py-6">
        {staticHeadlineMetricData.map((stat, index) => (
          <OverviewInfoCard
            key={index}
            icon={stat.icon}
            title={stat.title}
            value={stat.value}
            changeInPercent={stat.changeInPercent}
            period={translatedContent.period}
            unit={stat.unit}
            tooltipText={stat.tooltipText}
            error={stat?.error}
            isMobile={isMobile}
            isBackoffice={isBackoffice}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-stretch justify-between gap-4">
      {staticHeadlineMetricData.map((stat, index) => (
        <div key={index} className="mx-0 max-[580px]:mx-auto md:min-w-[250px]">
          <OverviewInfoCard
            key={index}
            icon={stat.icon}
            title={stat.title}
            value={stat.value}
            changeInPercent={stat.changeInPercent}
            period={translatedContent.period}
            unit={stat.unit}
            tooltipText={stat.tooltipText}
            error={stat?.error}
            isMobile={isMobile}
            isBackoffice={isBackoffice}
          />
        </div>
      ))}
    </div>
  );
};
